'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { useSwipeable } from 'react-swipeable';

interface CarouselProps {
    items: number;
    itemsMobile: number;
    children: any;
    nav?: boolean;
    bullets?: boolean;
    arrows?: boolean;
    insideBullets?: boolean;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    isTwoItemLayout?: boolean;
}

interface CarouselItemProps {
    children: any;
    width: string;
    className: string;
}

export const CarouselItem = (props: CarouselItemProps) => {
    return (
        <div className={props.className} style={{ width: props.width }}>
            {props.children}
        </div>
    );
};

const Carousel = (props: CarouselProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [nav, setNav] = useState(props.nav || false);
    const [bullets, setBullets] = useState(props.bullets || false);
    const [insideBullets, setInsideBullets] = useState(props.insideBullets || false);
    const [arrows, setArrows] = useState(props.arrows || false);
    const [itemsPerView, setItemsPerView] = useState(props.items);
    const [itemsPerViewMobile, setItemsPerViewMobile] = useState(props.itemsMobile);
    const [dekstopItemsPercentage, setDekstopItemsPercentage] = useState(0);
    const [screen, setScreen] = useState('D'); // D: Desktop, M: Mobile
    const [totalItems, setTotalItems] = useState(React.Children.count(props.children));
    
    const updateIndex = (index: any) => { 
        if (index < 0){
            index = 0;
        } else if (screen === 'D'){
            if (index >= (totalItems - itemsPerView)){
                index = totalItems - itemsPerView;
            }
        } else if (screen === 'M'){
            if (index >= (totalItems - itemsPerViewMobile)){
                index = totalItems - itemsPerViewMobile;
            }
        }
        
        setActiveIndex(index);
    };

    useEffect(() => {
        setTotalItems(React.Children.count(props.children));
    }, [props.children]);

    useEffect(() => {
        const screenWidth = window.innerWidth;
        if (screenWidth < 768) {
            setDekstopItemsPercentage(100 / itemsPerViewMobile);
            setScreen('M');
        } else {
            setDekstopItemsPercentage(100 / itemsPerView);
            setScreen('D');
        }
    }, [itemsPerView, itemsPerViewMobile]);

    // only when im on the active index, it should autoplay
    useEffect(() => {
        if (props.autoPlay) {
            const interval = setInterval(() => {
                updateIndex((prevIndex: number) => (prevIndex + 1) % totalItems);
            }, props.autoPlayInterval || 3000);

            return () => clearInterval(interval);
        }
    }, [props.autoPlay, props.autoPlayInterval, totalItems]);

    const swipeable = useSwipeable({
        onSwipedLeft: () => updateIndex(activeIndex + 1),
        onSwipedRight: () => updateIndex(activeIndex - 1),
    });

    return (
        <div className='carouselContainer'>
            <div className='carousel'  {...swipeable}>
                <div className='carousel__inner' style={{ transform: `translateX(-${activeIndex * (dekstopItemsPercentage)}%)` }}>
                    {React.Children.map(props.children, (child, index) => {
                        return React.cloneElement(child, { width: `${dekstopItemsPercentage}%`, className: `carousel__item` });
                    })}
                </div>
            </div>

            { bullets &&
                <div className="flex items-center justify-center gap-4 pr-32">
                    {Array.from({ length: totalItems }, (_, i) => i).map((index) => {
                        return <button key={index} className={`rounded-full h-2 w-2 cursor-pointer ${activeIndex === index ? 'bg-[#424B5A]' : 'border-2 border-[#C2D1D9]'}`} onClick={()=>updateIndex(index)} aria-label={`Ir a la diapositiva ${index + 1}`}></button>
                    })}
                </div>
            }

            { insideBullets &&
                <div className="flex relative -top-10 justify-center gap-4">
                    {Array.from({ length: totalItems }, (_, i) => i).map((index) => {
                        return <button key={index} className={`rounded-full h-2 w-2 cursor-pointer ${activeIndex === index ? 'bg-white' : 'border-2 border-gray-200]'}`} onClick={()=>updateIndex(index)} aria-label={`Ir a la diapositiva ${index + 1}`}></button>
                    })}
                </div>
            }

            { arrows &&
                <div className="flex absolute -bottom-6 left-1/2 transform -translate-x-1/2 space-x-4 z-10">
                    <button className="rounded-full bg-white border border-[#DFE4EA] h-14 w-14 flex items-center justify-center hover:shadow-md cursor-pointer" onClick={()=>updateIndex(activeIndex - 1)} aria-label="Anterior">
                        <FontAwesomeIcon icon={faChevronLeft} className="text-orange text-xl" />
                    </button>
                    <button className="rounded-full bg-yellow h-14 w-14 flex items-center justify-center hover:shadow-md cursor-pointer" onClick={()=>updateIndex(activeIndex + 1)} aria-label="Siguiente">
                        <FontAwesomeIcon icon={faChevronRight} className="text-white text-xl" />
                    </button>
                </div>
            }

            { nav && 
                <div className="carousel__nav">
                    <button type="button" className='carousel__nav--prev px-3 cursor-pointer' onClick={()=>updateIndex(activeIndex - 1)} aria-label="Anterior"><FontAwesomeIcon icon={faChevronLeft} /></button>
                    <button type="button" className='carousel__nav--next px-3 float-right cursor-pointer' onClick={()=>updateIndex(activeIndex + 1)} aria-label="Siguiente"><FontAwesomeIcon icon={faChevronRight} /></button>
                </div>
            }
        </div>
    );
};

export default Carousel;